import { useSubscriptionContext } from '@/contexts/SubscriptionContext';
import { useBarPackageAccess } from '@/hooks/useBarPackageAccess';

export type AccessSource = 'unlimited' | 'premium' | 'trial' | 'bar_package' | 'none';

export interface EffectiveJdsAccess {
  /** True if any source grants access. */
  hasAccess: boolean;
  /** Which source granted access (highest precedence wins when multiple apply). */
  source: AccessSource;
  /** True while either underlying query is loading. */
  isLoading: boolean;
  /** Convenience: source === 'trial' (Stripe-tier Trial only — NOT bar_package internal trial). */
  isTrial: boolean;
}

interface ComputeInputs {
  tierName: string | null | undefined;
  entitlementState: 'none' | 'purchased' | 'scheduled' | 'active' | 'expired' | 'trial';
  isLoading: boolean;
}

/**
 * Pure computation behind useEffectiveJdsAccess. Exposed for unit tests so we
 * don't need a React renderer. The hook below just wires the two upstream
 * hooks into this function.
 *
 * Precedence: unlimited > premium > trial > bar_package > none.
 */
export function computeEffectiveJdsAccess(inputs: ComputeInputs): EffectiveJdsAccess {
  const { tierName, entitlementState, isLoading } = inputs;

  if (tierName === 'Unlimited') {
    return { hasAccess: true, source: 'unlimited', isLoading, isTrial: false };
  }
  if (tierName === 'Premium') {
    return { hasAccess: true, source: 'premium', isLoading, isTrial: false };
  }
  if (tierName === 'Trial') {
    return { hasAccess: true, source: 'trial', isLoading, isTrial: true };
  }
  if (
    entitlementState === 'active' ||
    entitlementState === 'trial' ||
    entitlementState === 'scheduled'
  ) {
    return { hasAccess: true, source: 'bar_package', isLoading, isTrial: false };
  }
  return { hasAccess: false, source: 'none', isLoading, isTrial: false };
}

/**
 * Returns whether the current user has *effective JDS access* — the union of
 * Stripe subscription state and bar_package entitlement state, mirroring the
 * server-side bundling in src/lib/subscription.ts:598-619.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Two distinct "trial" semantics. DO NOT collapse these into one.
 * ────────────────────────────────────────────────────────────────────────────
 *
 *   source: 'trial'         → Stripe-tier Trial. tierName === 'Trial' on the
 *                              JDS Unlimited subscription. Lives in `subscriptions`
 *                              table, gated by Stripe webhooks.
 *
 *   source: 'bar_package'   → bar_package entitlement is granting access. The
 *                              entitlement's INTERNAL state may itself be
 *                              'trial' (7-day NextGen free trial, no Stripe
 *                              charge — see services/payment/src/routes/trial.ts)
 *                              OR 'active' OR 'scheduled'. The hook does NOT
 *                              expose that internal state through `source` —
 *                              consumers asking "does this user have access?"
 *                              don't need to know which bar_package lifecycle
 *                              stage granted it. If you need that distinction,
 *                              call useBarPackageAccess() directly.
 *
 * These two trials are different products with different fulfillment paths,
 * different DB tables (`subscriptions` vs `user_entitlements`), and different
 * expiration cadences. A user can be on a Stripe Trial AND a bar_package trial
 * simultaneously. The `source` enum picks ONE for analytics — order of
 * precedence: unlimited > premium > trial (Stripe) > bar_package > none.
 * Higher-tier Stripe subs win attribution over a finite-period bar_package
 * bundle because the Stripe sub is the explicit ongoing relationship; a
 * bar_package is a finite-period bundle that should not mask longer-lived
 * sources in analytics.
 *
 * Course access (lib/permissions.ts) intentionally does NOT use this hook —
 * course gates are narrower (Unlimited/Trial only, no Premium). See the
 * `useEffectiveJdsAccess` migration plan for the rationale.
 */
export function useEffectiveJdsAccess(): EffectiveJdsAccess {
  const { tierName, isLoading: subLoading } = useSubscriptionContext();
  const { entitlementState, isLoading: barLoading } = useBarPackageAccess();

  return computeEffectiveJdsAccess({
    tierName,
    entitlementState,
    isLoading: subLoading || barLoading,
  });
}
